<template>
  <OrderTemplate :step="2"
    :steps="[$t('order_seat_step_1'), $t('order_seat_step_2'), $t('order_seat_step_3'), $t('order_seat_step_4')]">
    <template v-slot:stepCard>
      <TicketTable :thead="thead" :tickets="reserveTickets">
        <template slot="title">{{ $t('confirm_st_confirm_cta') }}</template>
      </TicketTable>
    </template>

    <template v-slot:orderFooter>
      <v-snackbar v-model="snackbar" absolute bottom :timeout="5000">
        {{ $t('confirm_st_consecutive_seat_notification') }}
        <template v-slot:action="{ attrs }">
          <v-btn color="white" text v-bind="attrs" @click="snackbar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
      <v-col cols="12" sm="3" md="9" class="pb-2 pb-sm-0 d-flex justify-space-between">
        <v-btn class="font-weight-bold" @click="preStep()" color="primary" outlined style="border:2px solid" depressed
          :x-large="!$vuetify.breakpoint.xs">{{ $t('order_cancel') }}
        </v-btn>
        <v-btn v-if="!reserveResponse.finalizedSeats" class="font-weight-bold" color="primary" outlined
          style="border:2px solid" :disabled="!canNextStep" @click="updateSeat()" :x-large="!$vuetify.breakpoint.xs">{{
    $t('order_change_seat') }}</v-btn>
      </v-col>
      <v-spacer v-if="!$vuetify.breakpoint.xs" />

      <v-col cols="12" sm="6" md="3">
        <v-btn class="nextBtn" :elevation="0" :class="{ disabledBtn: !canNextStep }" :disabled="!canNextStep"
          @click="nextStep()" block :x-large="!$vuetify.breakpoint.xs">{{ $t('next_step') }}</v-btn>
      </v-col>
    </template>
    <CustomCard />
  </OrderTemplate>
</template>

<script>
import { getProducts } from "@/api/static";
import OrderTemplate from "./OrderTemplate.vue";
import TicketTable from "./components/TicketsTable.vue";
import { mapGetters, mapActions } from "vuex";
import { encrypt } from "@/vendor/crypto";
import { CurrentReservedMixins } from "@/shared/mixins"
import CC from "@/api/config-center";
import TM from "@/api/ticket-management";

class SeatArray extends Array {
  isConsecutive() {
    for (const seat of this) {
      if (!seat) return false;
    }
    const rootMap = new Map();
    for (const seat of this) {
      rootMap.set(seat.index, seat.index);
    }
    for (const seat of this) {
      if (seat.nextIndex === undefined) continue;
      let root1 = this.findRoot(seat.index, rootMap);
      let root2 = this.findRoot(seat.nextIndex, rootMap);
      rootMap.set(root1, root2);
    }
    let result;
    for (const val of rootMap.values()) {
      const rootVal = this.findRoot(val, rootMap);
      if (result === undefined) result = rootVal;
      if (result !== rootVal) return false;
    }
    return true;
  }

  findRoot(index, rootMap) {
    if (rootMap.get(index) === undefined) {
      rootMap.set(index, index);
      return index;
    }
    if (rootMap.get(index) === index) return index;
    rootMap.set(index, this.findRoot(rootMap.get(index), rootMap));
    return rootMap.get(index);
  }
}

function isConsecutive(targetSeats) {
  if (!targetSeats.every(seat => seat)) return false;
  let indexSet = new Set();
  let nextSet = new Set();
  for (let seat of targetSeats) {
    let index = seat.index;
    let next = seat.nextIndex;
    if (indexSet.size === 0 && nextSet.size === 0) {
      indexSet.add(index);
      if (next !== undefined) nextSet.add(next);
    } else if (next === undefined && !nextSet.has(index)) {
      indexSet.add(index);
    } else if (next !== undefined) {
      if (indexSet.has(next)) {
        indexSet.delete(next);
      } else {
        nextSet.add(next);
      }
      if (nextSet.has(index)) {
        nextSet.delete(index);
      } else {
        indexSet.add(index);
      }
    }
  }
  if (indexSet.size > 1) return false;
  return true;
}

export default {
  mixins: [CurrentReservedMixins],
  components: {
    OrderTemplate,
    TicketTable
  },
  data() {
    return {
      name: "",
      email: "",
      snackbar: false,
      isReleased: false,
      consecutiveSeats: true,
      phone: "",
      selectedDiscount: "0",
      couponCode: "",
      reserveTickets: [],
      seats: [],
      products: [],
      tickets: [],
      coupon: {
        get: false,
        name: "",
        discount: 0,
        error: ""
      }
    };
  },
  metaInfo() {
    return {
      title: `${this.$t('confirm_st_title')} | ${this.$t('app_name')}`,
      script: [{
        type: 'application/ld+json',
        json: {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": `${this.$t('confirm_st_title')} | ${this.$t('app_name')}`,
          "url": window.location.href
        }
      }]
    }
  },
  computed: {
    ...mapGetters({
      campaign: "event/campaign",
      sessionInfo: "session/sessionInfo",
      productData: "session/productData",
      ticketsData: "session/ticketsData",
      isPopState: "order/isPopState",
      reserveResponse: "order/reserveResponse",
      updateResponse: "order/updateResponse",
      areaAssignment: "order/areaAssignment",
      seatAssignment: "order/seatAssignment",
      selectedArea: "order/selectedArea",
      pageData: "order/pageData",
    }),
    thead() {
      return [
        { name: "ticketAreaName", label: this.$t('orderlist_ticketarea_name'), col: 3, class: this.$vuetify.breakpoint.xs ? 'pl-4' : 'px-8' },
        { name: "seat", label: this.$t('orderlist_ticketarea_seat'), col: 3, class: this.$vuetify.breakpoint.xs ? 'text-right pr-4' : 'text-center' },
        { name: "name", label: this.$t('orderlist_product_name'), col: 3, class: this.$vuetify.breakpoint.xs ? 'pl-4 mt-4' : 'text-center' },
        { name: "price", label: this.$t('orderlist_product_sum'), col: 3, class: this.$vuetify.breakpoint.xs ? 'text-right pr-4 mt-4' : 'text-center' },
      ];
    },
    canNextStep() {
      if (this.selectedDiscount == "0") return true;
      if (this.selectedDiscount == "1")
        return !this.coupon.error && this.coupon.discount;
      else if (this.selectedDiscount) return true;
      return false;
    },
  },
  watch: {
    isPopState: {
      deep: true,
      handler: function (newVal, old) {
        if (newVal !== old && !newVal) {
          this.init();
          this.setPopState(false)
        }
      }
    },
    ticketsData(newVal, old) {
      this.tickets = newVal
    },
    seats(newVal, preVal) {
      if (newVal.length) {
        let isConsecuetive = true;
        if (newVal.length > 1) {
          newVal.forEach((s, i) => {
            if (i > 0 && s.index !== newVal[i - 1].nextIndex) {
              isConsecuetive = false;
            }
          })
        }

        if (!isConsecuetive) this.snackbar = true;
      }
    },
    selectedDiscount(newVal, preVal) {
      if (preVal == "1") {
        this.couponCode = "";
        this.coupon = {
          get: false,
          name: "",
          discount: 0,
          error: ""
        };
      }
    },
    couponCode() {
      if (this.coupon.get) {
        this.coupon = {
          get: false,
          name: "",
          discount: 0,
          error: ""
        };
      }
    }
  },
  created() {
    this.$store.dispatch("setOverlayLoading", true);
  },
  methods: {
    ...mapActions({
      setCountDownTimer: "order/countDownTimer",
      getLoginInfo: "user/getInfo",
      updateOrder: "order/updateOrder",
      showDialog: "dialog/show",
      resetOrderState: "order/resetOrderState",
    }),
    async init() {
      const { hasReservedOrder } = await this.checkReservedOrder()
      if (!this.reserveResponse.orderId) {
        if (hasReservedOrder) await this.setReservedData()
        else
          this.$router
            .push({
              name: "Order1",
              params: {
                eventId: this.$route.params.eventId,
                sessionId: this.$route.params.sessionId,
              },
            })
            .catch(() => { });
      }

      this.reserveTickets = (this.areaAssignment && this.seatAssignment) ? await this.setSeatArea() : await this.setTicket()

      const seats = this.reserveResponse.products.reduce((a, p) => {
        a = a.concat(p.seats);
        return a;
      }, []);

      const seatsArray = SeatArray.from(seats);
      const result2 = seatsArray.isConsecutive();
      const result = isConsecutive(seatsArray)

      if (!result) this.consecutiveSeats = false;

      if (!this.consecutiveSeats) {
        this.showDialog({
          title: this.$t("order_step_1_no_consecutive_seats_title"),
          content: this.$t("order_step_1_no_consecutive_seats_content"),
          buttons: [
            {
              text: this.$t("order_step_1_reserve_dialog_cta"),
              style: {
                depressed: true,
                block: true,
                outlined: false,
                color: "primary"
              },
              action: () => { }
            }
          ]
        });
      }

      this.reserveTicket({
        ...this.reserveResponse,
        tickets: this.reserveTickets
      })

      if (this.sessionInfo.realNameRegistration) {
        this.applicantLimit = this.reserveTickets.length
      }

      if (Object.keys(this.reserveResponse).length === 0) {
        this.$router.push({
          name: 'Order1',
          params: {
            eventId: this.$route.params.eventId,
            sessionId: this.$route.params.sessionId
          }
        }).catch(() => { });
        return;
      }

      this.setCountDownTimer();
      this.$store.dispatch("setOverlayLoading", false);

      this.$gtm.trackView(`確認座位-${this.sessionInfo.name}`, this.$route.path);
    },
    async productInit() {
      if (!this.productData || !this.productData.length || this.productData && this.productData[0].sessionId !== this.$route.params.sessionId) {
        const { data } = await getProducts(this.$route.params.eventId);
        let products = data.products.filter(
          ({ sessionId }) => {
            let sid = (/^s[0-9]{9}$/).test(sessionId) ? encrypt(sessionId) : sessionId
            return sid == this.$route.params.sessionId
          }
        );
        const [{ data: ProductInfo }, getInfosErr] = await CC.getInfos({
          productId: products.map(p => p.productId),
        });
        products = products.map(prod => {
          const info = ProductInfo.result.product.find(p => p.id === prod.productId)
          return {
            ...info,
            ...prod
          }
        })

        this.setTicketProduct({
          products,
          tickets: this.productData.map((s) => {
            s.price = Number(s.price);
            s.remain = s.count;
            s.count = 0;
            return s;
          })
        })

        this.$forceUpdate()
        return products
      } else {
        return this.productData
      }
    },
    async setSeatArea() {
      const productData = await this.productInit()
      const seats = this.reserveResponse.products.reduce(
        (accr, reserve, index) => {
          let seats = [];
          const p = productData.find(p => p.productId === reserve.productId)
          seats = reserve.seats.reduce((ary, seat) => {
            ary.push({
              ...p,
              ...reserve,
              ...seat,
              ticketName: p.name,
              count: 1,
            });
            return ary;
          }, []);
          accr = accr.concat(seats);
          return accr;
        },
        []
      );

      return seats.map((seat, i) => {
        let ticket = this.tickets.find((t) => t.productId === seat.productId);

        return { ...seat, ...ticket };
      });
    },
    async setTicket() {
      const productData = await this.productInit()

      return this.reserveResponse.products.reduce((accr, reserve, index) => {
        let reserves = [];
        let selectedTicket = this.tickets.find(
          (t) => t.productId === reserve.productId
        );
        const p = productData.find(p => p.productId === reserve.productId)

        this.applicantLimit += reserve.count;
        reserves.push({
          ...p,
          ...selectedTicket,
          ...reserve,
        });
        accr = accr.concat(reserves);
        return accr
      }, [])
    },
    async preStep() {
      this.isReleased = true
      if (this.reserveResponse.orderId) {
        this.$store.dispatch("setOverlayLoading", true);
        const { data } = await TM.ticketRelease({
          orderId: this.reserveResponse.orderId
        });
        this.isReleased = true
        await this.resetOrderState();
        // console.log(`${String.fromCodePoint(128049)} [TM] release`, data);
      }

      this.$router.push({
        name: "Order1",
        params: { sessionId: this.$route.params.sessionId }
      }).catch(() => { });
      this.$store.dispatch("setOverlayLoading", false);
    },
    async updateSeat() {
      this.$store.dispatch("setOverlayLoading", true);

      this.$router.push({
        name: "SelSt",
        params: { sessionId: this.$route.params.sessionId }
      }).catch(() => { });
      this.$store.dispatch("setOverlayLoading", false);
    },
    async nextStep() {
      this.$store.dispatch("setOverlayLoading", true);
      let selectRadio = Number(this.selectedDiscount);
      // no discount
      this.$router.push({ name: "Order2", params: { sessionId: this.$route.params.sessionId } }).catch(() => { });
    },
    inputField() {
      this.coupon.error = "";
    }
  },
  mounted() {
    this.init();
    this.setPopState(false)
  },
  beforeRouteLeave(to, from, next) {
    if (!['SelSt', 'Order2'].includes(to.name) && this.reserveResponse.orderId && !this.isReleased) {
      next(false)
      this.leaveConfirmation(to)
      return false;
    }
    next()
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  label {
    font-weight: 500;
  }

  .v-input--selection-controls__input+.v-label {
    margin-left: 20px;
  }

  .v-input__slot {
    margin-bottom: 0;
  }

  .theme--light.v-input--is-disabled input {
    color: #444444;
  }

  .discountGroup {
    color: #8d8d8d;

    label {
      color: #8d8d8d;
    }

    .v-item--active {
      label {
        color: #444444;
      }
    }
  }

  .order-footer .row {
    margin: 0 -15px;

    [class*='col-'],
    .col {
      padding: 0 15px;
    }
  }
}

// xs and sm
@media screen and (max-width: 960px) {
  .rwd-padding {
    padding: 0 16px;
  }
}

::v-deep {
  .v-snack__wrapper {
    bottom: 100%
  }

  @media screen and (max-width: 600px) {
    .order-footer {
      .row {
        margin: 0 -6px;

        [class*='col-'],
        .col {
          padding: 0 6px;
        }
      }
    }
  }
}

// greater than sm
@media only screen and (min-width: 960px) {
  .rwd-padding {
    padding: 0 32px;
  }

  .order-footer .v-btn.v-btn--outlined:not(.v-btn--disabled) {
    min-width: 270px;
  }
}

.order-footer .v-btn,
.order-footer .v-btn.v-btn--outlined:not(.v-btn--disabled) {
  padding-left: 12px;
  padding-right: 12px;
}
</style>
